/**
 * Formats a number with specified precision, unit, and formatting options
 * @param options - Formatting options
 * @returns Formatted number string
 */
interface FormatNumberOptions {
  /** Number to format */
  number?: number | string
  /** Number of decimal places */
  precision?: number
  /** Unit to append/prepend to the number */
  unit?: string
  /** Automatically adjust precision based on number size */
  adaptablePrecision?: boolean
  /** Place unit before the number instead of after */
  unitOnLeft?: boolean
}

export const formatNumber = ({
  number = 0,
  precision = 0,
  unit = '',
  adaptablePrecision = false,
  unitOnLeft = false
}: FormatNumberOptions): string => {
  const parsedUnit = unit
    ? unit === '%'
      ? unit
      : unitOnLeft
      ? `${unit}\u00A0`
      : `\u00A0${unit}`
    : ''
  const n = number
  let newPrecision = precision

  if (adaptablePrecision) {
    const numValue = Number(n)
    if (numValue < 0.1) {
      newPrecision += 2
      if (numValue < 0.01) {
        newPrecision++
        if (numValue < 0.001) {
          newPrecision = 0
        }
      }
    }
  }

  let workingPrecision = precision
  if (
    workingPrecision === 0 &&
    Number(n) > 0 &&
    Number(n) < 1 &&
    !adaptablePrecision
  ) {
    workingPrecision = 2
  }

  const numStr =
    typeof number === 'number' || typeof number === 'string'
      ? number.toString()
      : '0'
  const newNumber = Number(numStr.replace(',', '.'))

  if (isNaN(newNumber)) {
    return Number(0).toFixed(workingPrecision)
  }

  const fixedNumber = Number.parseFloat(newNumber.toString()).toFixed(
    newPrecision || workingPrecision
  )
  const numberString = fixedNumber.toString()

  let formattedNumber = ''
  let count = 0
  for (let i = numberString.length - 1; i >= 0; i--) {
    if (numberString[i] === '.') {
      formattedNumber = '.' + formattedNumber
      count = 0 // Reset the count after encountering a decimal point
    } else {
      formattedNumber = numberString[i] + formattedNumber
      count++
      if (count % 3 === 0 && i !== 0) {
        formattedNumber = '\u00A0' + formattedNumber
      }
    }
  }
  const parsedNumber = formattedNumber

  const parsedNumberWithUnit = unitOnLeft
    ? parsedUnit + parsedNumber
    : parsedNumber + parsedUnit
  return parsedNumberWithUnit
}
